// Демо-данные окна «Рассылки»: папки по статусам, метки и готовые рассылки,
// которые появляются, когда в окне включён демо-режим.
import { t } from "@/lib/i18n";

export type BroadcastStatus = "draft" | "scheduled" | "in_progress" | "completed" | "failed";

export type Broadcast = {
  id: string;
  name: string;
  status: BroadcastStatus;
  channels: string[];
  audience: number;
  date: string;
  author: string;
  labels: string[];
};

export const broadcastStatuses: { id: BroadcastStatus; label: string; dot: string; pill: string }[] = [
  { id: "draft", label: t("Черновик"), dot: "bg-foreground/30", pill: "bg-foreground/[.06] text-foreground/70" },
  { id: "scheduled", label: t("Запланировано"), dot: "bg-amber-500", pill: "bg-amber-500/10 text-amber-700" },
  { id: "in_progress", label: t("Отправляется"), dot: "bg-orange-500", pill: "bg-orange-500/10 text-orange-700" },
  { id: "completed", label: t("Завершено"), dot: "bg-emerald-500", pill: "bg-emerald-500/10 text-emerald-700" },
  { id: "failed", label: t("Ошибка"), dot: "bg-red-500", pill: "bg-red-500/10 text-red-700" },
];

export const broadcastFolders: { id: "all" | BroadcastStatus; label: string; dot: string }[] = [
  { id: "all", label: t("Все"), dot: "bg-primary" },
  ...broadcastStatuses.map((s) => ({ id: s.id, label: s.label, dot: s.dot })),
];

export const broadcastLabels = [
  t("Новости продукта"),
  t("Акции и скидки"),
  t("Онбординг"),
  t("VIP-клиенты"),
  t("Возврат клиентов"),
  t("Обновления тарифа"),
];

export const broadcastChannels = [
  { id: "whatsapp", label: "WhatsApp", icon: "ri-whatsapp-line" },
  { id: "telegram", label: "Telegram", icon: "ri-telegram-line" },
  { id: "email", label: t("Почта"), icon: "ri-mail-line" },
  { id: "sms", label: "SMS", icon: "ri-message-2-line" },
];

// ── тексты пустого состояния ──

export const broadcastEmpty = {
  title: t("Вы ещё не создали ни одной рассылки"),
  text: t("Рассылка отправляет одно сообщение сразу во множество диалогов — по меткам, этапам жизненного цикла или выбранным вручную контактам."),
  primary: t("Новая рассылка"),
  secondary: t("Узнать больше"),
};

export const broadcastFolderNotes: Record<string, string> = {
  all: t("Все рассылки рабочего пространства: черновики, запланированные и уже отправленные."),
  draft: t("Черновики, которые ещё не отправлены: текст, метки и аудиторию можно менять в любой момент."),
  scheduled: t("Рассылки с датой в будущем — уйдут автоматически в выбранное время."),
  in_progress: t("Рассылки, которые прямо сейчас отправляются получателям."),
  completed: t("Завершённые рассылки с итоговой статистикой доставки и ответов."),
  failed: t("Рассылки, которые не удалось отправить: проверьте каналы и повторите попытку."),
};

export const broadcastHints = [
  {
    icon: "ri-group-line",
    title: t("Кому уходит рассылка"),
    text: t("Получателей выбирают по меткам, этапу жизненного цикла или вручную — можно исключить тех, кто уже ответил."),
  },
  {
    icon: "ri-time-line",
    title: t("Когда отправлять"),
    text: t("Рассылка уходит сразу или по расписанию: в календаре видно, в какой день и час она стартует."),
  },
  {
    icon: "ri-bar-chart-box-line",
    title: t("Что показывает статус"),
    text: t("«Отправляется» — идёт рассылка, «Завершено» — все сообщения доставлены, «Ошибка» — канал отклонил часть отправок."),
  },
];

// ── готовые рассылки демо-режима ──

export const demoBroadcasts: Broadcast[] = [
  {
    id: "bc-1",
    name: t("Чёрная пятница: подборка тарифов"),
    status: "completed",
    channels: ["whatsapp", "telegram"],
    audience: 1284,
    date: "2026-10-12",
    author: t("Мария Петрова"),
    labels: [t("Акции и скидки")],
  },
  {
    id: "bc-2",
    name: t("Ноябрьские обновления Hub"),
    status: "scheduled",
    channels: ["email", "telegram"],
    audience: 2140,
    date: "2026-11-03",
    author: t("Алексей Ким"),
    labels: [t("Новости продукта")],
  },
  {
    id: "bc-3",
    name: t("Онбординг новых клиентов — неделя 1"),
    status: "in_progress",
    channels: ["whatsapp", "email"],
    audience: 320,
    date: "2026-10-29",
    author: t("Ольга Смирнова"),
    labels: [t("Онбординг")],
  },
  {
    id: "bc-4",
    name: t("Возврат клиентов, молчавших 3 месяца"),
    status: "draft",
    channels: ["whatsapp"],
    audience: 0,
    date: "2026-10-28",
    author: t("Дмитрий Волков"),
    labels: [t("Возврат клиентов")],
  },
  {
    id: "bc-5",
    name: t("VIP-клиентам: личный менеджер"),
    status: "scheduled",
    channels: ["whatsapp", "telegram", "email"],
    audience: 86,
    date: "2026-11-10",
    author: t("Ирина Соколова"),
    labels: [t("VIP-клиенты"), t("Новости продукта")],
  },
  {
    id: "bc-6",
    name: t("Вебинар «Единые входящие за вечер»"),
    status: "completed",
    channels: ["email"],
    audience: 1740,
    date: "2026-10-08",
    author: t("Мария Петрова"),
    labels: [t("Новости продукта")],
  },
  {
    id: "bc-7",
    name: t("Смена тарифа Growth — предупреждение"),
    status: "failed",
    channels: ["sms", "email"],
    audience: 512,
    date: "2026-10-22",
    author: t("Алексей Ким"),
    labels: [t("Обновления тарифа")],
  },
  {
    id: "bc-8",
    name: t("Скидка 15% на второй номер"),
    status: "completed",
    channels: ["whatsapp"],
    audience: 940,
    date: "2026-10-24",
    author: t("Ольга Смирнова"),
    labels: [t("Акции и скидки")],
  },
  {
    id: "bc-9",
    name: t("Проверка канала Telegram перед запуском"),
    status: "draft",
    channels: ["telegram"],
    audience: 0,
    date: "2026-10-27",
    author: t("Дмитрий Волков"),
    labels: [],
  },
];